// Bootstrap effects for FlowchartBuilder that grant access from a URL: a
// share-link token (?token=...) or an email invitation (?invite=...&type=...).
// Extracted out of the component body. Pure effect-registration hook — no
// internal state, nothing returned.
//
// Both effects close over `load`, which is defined later in FlowchartBuilder.tsx
// as a plain (non-memoized) function — that works fine as a closure reference
// (resolved lazily, once the effect body actually runs), but once `load` is a
// hook PARAMETER instead, this hook's call site in FlowchartBuilder.tsx must be
// placed textually AFTER `const load = ...` is assigned, or it's a temporal-dead-
// zone error rather than a deferred reference.
import { useEffect } from 'react';
import { logger } from './lib/logger';
import supabase from './supabase';
import { useT } from './i18n/index';
import type { SavedFlowchart } from './types/index';

interface UseShareAndInvitesParams {
  user: { id: string; email?: string } | null | undefined;
  load: (f: SavedFlowchart) => void;
  setCurrentId: (id: string | null) => void;
  setTitle: (t: string) => void;
}

export function useShareAndInvites(params: UseShareAndInvitesParams): void {
  const { user, load, setCurrentId, setTitle } = params;
  const { t } = useT();

  // Verify share token on mount
  useEffect(() => {
    const verifyToken = async () => {
      // Get flowchart ID from URL
      const pathParts = window.location.pathname.split('/');
      const urlFlowchartId = pathParts[pathParts.length - 1];

      // Get token from URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const shareToken = urlParams.get('token');

      if (!urlFlowchartId || !shareToken || urlFlowchartId.length < 30) {
        return; // No valid share token in URL
      }

      // Wait for user to be loaded (important for share links!)
      if (!user) {
        logger.log('Waiting for user to login before verifying share token...');
        return;
      }

      // Import and verify share token
      const { verifyShareToken } = await import('./types/sharingUtils');
      const result = await verifyShareToken(urlFlowchartId, shareToken);

      if (result && result.valid) {
        // Fetch the flowchart data from database
        try {
          const { data: flowchartData, error } = await supabase
            .from('flowcharts')
            .select('*')
            .eq('id', urlFlowchartId)
            .single();

          if (error) {
            logger.error('Error loading shared flowchart:', error);
            alert(t('couldNotLoadShared'));
            return;
          }

          if (!flowchartData) {
            alert(t('flowchartNotFound'));
            return;
          }

          // Load the flowchart (hook re-checks permissions when currentId changes)
          setCurrentId(flowchartData.id);
          setTitle(flowchartData.title);
          load(flowchartData);
        } catch (err) {
          logger.error('Error loading flowchart:', err);
          alert(t('couldNotLoadShared'));
        }
      } else {
        alert(t('invalidShareLink'));
      }
    };

    verifyToken();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle email invitation token on mount (?invite=<token>)
  useEffect(() => {
    const acceptInvite = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const inviteToken = urlParams.get('invite');
      const inviteType = urlParams.get('type') ?? 'flowchart'; // 'flowchart' | 'project'
      if (!inviteToken) return;

      if (!user) return;

      try {
        if (inviteType === 'project') {
          // Grant access to the project via a SECURITY DEFINER function
          const { data: result, error: rpcErr } = await supabase
            .rpc('accept_project_invitation', { p_invite_token: inviteToken });

          if (rpcErr || !result?.success) {
            const msg = result?.error ?? rpcErr?.message ?? 'Unknown error';
            alert(t('couldNotAcceptInvitation', { msg }));
            return;
          }

          alert(t('projectAccessGranted', { role: result.role }));
        } else {
          // Flowchart invitation — use a SECURITY DEFINER function so
          // the invitee can grant themselves access without being the owner
          const { data: result, error: rpcErr } = await supabase
            .rpc('accept_flowchart_invitation', { p_invite_token: inviteToken });

          if (rpcErr || !result?.success) {
            const msg = result?.error ?? rpcErr?.message ?? 'Unknown error';
            alert(t('couldNotAcceptInvitation', { msg }));
            return;
          }

          const { data: flowchartData, error: fcErr } = await supabase
            .from('flowcharts')
            .select('*')
            .eq('id', result.flowchart_id)
            .single();

          if (fcErr || !flowchartData) {
            alert(t('accessGrantedOpenManually'));
            return;
          }

          setCurrentId(flowchartData.id);
          setTitle(flowchartData.title);
          load(flowchartData);
        }

        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);

      } catch (err: any) {
        logger.error('Error accepting invitation:', err);
        alert(t('somethingWentWrongInvitation'));
      }
    };

    acceptInvite();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
}
